import { Timestamp } from "firebase/firestore/lite";
import { clientService } from "../client/service";
import { DateTime } from "luxon";
import { DbBusinessClient } from "../client/models";
import { bookingService } from "../booking/service";
import { DbVisit } from "../visit/models";
import { CampaignFilter, CampaignFilterClient, ServiceRecord } from "./models";
import {
  groupVisitsByUser,
  extractBookingIdsFromVisits,
  matchesFilter,
} from "./filter-utils";
import { filterClientsByEmail } from "./provider-helpers";

class ClientFilterService {
  // Filter clients based on campaign criteria
  private filterClients(
    clients: CampaignFilterClient[],
    filter: CampaignFilter,
  ): CampaignFilterClient[] {
    return clients.filter((client) => matchesFilter(client, filter));
  }

  // filter clients based on campaign criteria
  async runCampaignFilter(
    filter: CampaignFilter,
    businessId: string,
    isEmailCampaign: boolean = false,
  ): Promise<{
    filteredClients: CampaignFilterClient[];
    totalClients: number;
  }> {
    // prepare start and end dates
    let startDate = DateTime.now();
    let endDate = DateTime.now().endOf("day");
    if (filter.duration && filter.duration.value > 0) {
      startDate = startDate
        .minus({ days: filter.duration.value - 1 })
        .startOf("day");
    } else if (filter.filter_start_date && filter.filter_end_date) {
      startDate = DateTime.fromMillis(
        filter.filter_start_date.toMillis(),
      ).startOf("day") as DateTime;
      endDate = DateTime.fromMillis(filter.filter_end_date.toMillis()).endOf(
        "day",
      ) as DateTime;
    }

    const contestantClients: CampaignFilterClient[] = [];

    let allClients = await clientService.getAllBusinessClients(businessId);

    if (isEmailCampaign) {
      allClients = filterClientsByEmail(allClients);
    }

    const allClientIds = allClients.map((client) => {
      return client.id;
    });
    const users = await clientService.getBusinessClientsByIds(
      businessId,
      allClientIds,
    );
    // prepare client with filter properties
    allClients.forEach((client: DbBusinessClient) => {
      contestantClients.push({
        client_id: client.id,
        last_service_date: null,
        total_spent: 0,
        gender: users.find((user) => user.id === client.id)?.gender || null,
        onboarded_at: client.created_at,
        services: [],
        no_of_bookings_per_year: 0,
        used_services: {
          service_id: "",
          booking_frequency: 0,
          last_booked_at: new Timestamp(0, 0),
        },
        no_of_bookings: 0,
      });
    });

    if (filter.type === "duration") {
      const hasClientPresence =
        filter.client_presence &&
        (filter.client_presence.new_clients ||
          filter.client_presence.not_booked_in_days);

      const hasGender = filter.gender && filter.gender !== "all";

      if (!hasClientPresence && !hasGender) {
        return {
          filteredClients: contestantClients,
          totalClients: allClients.length,
        };
      }
      if (filter.client_presence?.not_booked_in_days) {
        const visits = await bookingService.getConfirmedBookingsFromVisits(
          businessId,
          startDate,
          endDate,
        );

        contestantClients.forEach((client: CampaignFilterClient) => {
          const clientVisits = visits.filter(
            (visit: DbVisit) => visit.user_id === client.client_id,
          );

          if (clientVisits.length > 0) {
            client.last_service_date = clientVisits[0]
              .booking_date as unknown as Timestamp;
          }
        });
      }
    } else if (filter.type === "custom") {
      const hasClientPresence =
        filter.client_presence &&
        (filter.client_presence.new_clients ||
          filter.client_presence.not_booked_in_days ||
          filter.client_presence.loyal_clients ||
          filter.client_presence.loyal_but_lost_clients);

      const hasServiceUsage =
        filter.service_usage &&
        (filter.service_usage.used_service_id ||
          filter.service_usage.not_used_service_id ||
          filter.service_usage.not_tried_service_id ||
          filter.service_usage.left_over_details);

      const hasAmountSpent =
        filter.amount_spent &&
        (filter.amount_spent.more_than !== null ||
          filter.amount_spent.less_than !== null);

      const hasGender = filter.gender && filter.gender !== "all";

      if (
        !hasClientPresence &&
        !hasServiceUsage &&
        !hasAmountSpent &&
        !hasGender
      ) {
        return {
          filteredClients: [],
          totalClients: 0,
        };
      }
      // Loyal clients
      if (filter.client_presence?.loyal_clients) {
        const startDate = DateTime.now().startOf("day").minus({
          years: 1,
        });
        const endDate = DateTime.now().endOf("day");
        const visits = await bookingService.getConfirmedBookingsFromVisits(
          businessId,
          startDate,
          endDate,
        );
        // group by users
        const visitsByUser = groupVisitsByUser(visits);
        contestantClients.map((client: CampaignFilterClient) => {
          const clientVisits = visitsByUser[client.client_id] || [];
          client.no_of_bookings_per_year = clientVisits.length;
        });
      }
      // Loyal but lost clients
      if (
        filter.client_presence?.loyal_but_lost_clients?.min_booking_duration &&
        filter.client_presence?.loyal_but_lost_clients?.min_booking_duration
          .unit &&
        filter.client_presence?.loyal_but_lost_clients?.min_booking_duration
          .value &&
        filter.client_presence?.loyal_but_lost_clients?.lost_duration &&
        filter.client_presence?.loyal_but_lost_clients?.lost_duration.unit &&
        filter.client_presence?.loyal_but_lost_clients?.lost_duration.value
      ) {
        const analysisDuration =
          filter.client_presence.loyal_but_lost_clients.min_booking_duration;
        const lostDuration =
          filter.client_presence.loyal_but_lost_clients.lost_duration;

        if (analysisDuration && lostDuration) {
          const analysisStartDate = DateTime.now()
            .startOf("day")
            .minus({
              [analysisDuration.unit]: analysisDuration.value,
            });
          const visits = await bookingService.getConfirmedBookingsFromVisits(
            businessId,
            analysisStartDate,
            endDate,
          );

          const lostStartDate = DateTime.now()
            .startOf("day")
            .minus({
              [lostDuration.unit]: lostDuration.value,
            });
          const visitsByUser = groupVisitsByUser(visits);
          contestantClients.map((client: CampaignFilterClient) => {
            const clientVisits = visitsByUser[client.client_id] || [];
            if (clientVisits.length === 0) return;
            const lostVisits = clientVisits.filter(
              (visit) =>
                (visit.booking_date?.seconds ?? 0) > lostStartDate.toSeconds(),
            );

            if (lostVisits.length > 0) return;
            client.no_of_bookings = clientVisits.length;
            client.last_service_date = clientVisits[0]
              .booking_date as unknown as Timestamp;
          });
        }
      }
      // Used/Not used services
      if (
        filter.service_usage?.used_service_id &&
        filter.service_usage?.not_used_service_id
      ) {
        const visits = await bookingService.getConfirmedBookingsFromVisits(
          businessId,
          null,
          null,
        );

        const allBookingIds: string[] = extractBookingIdsFromVisits(visits);

        const bookings = await bookingService.getBookingsByIds(
          businessId,
          allBookingIds,
        );

        const visitsByUser = groupVisitsByUser(visits);
        contestantClients.map((client: CampaignFilterClient) => {
          const clientVisits = visitsByUser[client.client_id] || [];
          const allServices: string[] = [];

          clientVisits.forEach((visit) => {
            visit.services.forEach((service) => {
              const booking = bookings.find((b) => b.id === service.booking_id);
              if (booking) {
                allServices.push(booking.service_id);
              }
            });
          });

          const filteredServices = allServices.filter(
            (serviceId) =>
              serviceId === filter.service_usage?.used_service_id &&
              serviceId !== filter.service_usage?.not_used_service_id,
          );
          client.services = [...new Set(filteredServices)];
        });
      }
      // Left using services
      if (
        filter.service_usage?.left_over_details &&
        filter.service_usage?.left_over_details.service_id
      ) {
        const visits = await bookingService.getConfirmedBookingsFromVisits(
          businessId,
          null,
          null,
        );

        const allBookingIds: string[] = extractBookingIdsFromVisits(visits);

        const bookings = await bookingService.getBookingsByIds(
          businessId,
          allBookingIds,
        );
        const visitsByUser = groupVisitsByUser(visits);
        contestantClients.map((client: CampaignFilterClient) => {
          const clientVisits = visitsByUser[client.client_id] || [];
          if (clientVisits.length === 0) return;

          // count bookings based on provided service_id
          const providedServiceBookings: any[] = [];
          clientVisits.forEach((visit) => {
            visit.services.forEach((service) => {
              const booking = bookings.find((b) => b.id === service.booking_id);
              if (
                booking &&
                booking.service_id ===
                  filter.service_usage?.left_over_details?.service_id
              ) {
                providedServiceBookings.push(booking);
              }
            });
          });

          if (providedServiceBookings.length === 0) return;
          const sortedBookings = providedServiceBookings.sort(
            (a, b) =>
              (b.booking_date?.seconds ?? 0) - (a.booking_date?.seconds ?? 0),
          );

          client.used_services = {
            service_id:
              filter.service_usage?.left_over_details?.service_id || "",
            booking_frequency: providedServiceBookings.length,
            last_booked_at: sortedBookings[0]
              .booking_date as unknown as Timestamp,
          } as ServiceRecord;
        });
      }
      // Not tried service
      if (
        filter.service_usage?.not_tried_service_id &&
        !(
          filter.service_usage?.used_service_id &&
          filter.service_usage?.not_used_service_id
        )
      ) {
        const visits = await bookingService.getConfirmedBookingsFromVisits(
          businessId,
          null,
          null,
        );

        const allBookingIds: string[] = extractBookingIdsFromVisits(visits);

        const bookings = await bookingService.getBookingsByIds(
          businessId,
          allBookingIds,
        );
        const visitsByUser = groupVisitsByUser(visits);
        contestantClients.map((client: CampaignFilterClient) => {
          const clientVisits = visitsByUser[client.client_id] || [];
          const allServices: string[] = [];

          clientVisits.forEach((visit) => {
            visit.services.forEach((service) => {
              const booking = bookings.find((b) => b.id === service.booking_id);
              if (booking) {
                allServices.push(booking.service_id);
              }
            });
          });

          client.services = allServices;
        });
      }
      // Amount spent
      if (
        filter.amount_spent &&
        (filter.amount_spent.more_than !== null ||
          filter.amount_spent.less_than !== null)
      ) {
        const visits = await bookingService.getConfirmedBookingsFromVisits(
          businessId,
          null,
          null,
        );
        const visitsByUser = groupVisitsByUser(visits);
        contestantClients.map((client: CampaignFilterClient) => {
          const clientVisits = visitsByUser[client.client_id] || [];
          client.total_spent = clientVisits.reduce(
            (acc, visit) => acc + visit.financial_summary.total_amount,
            0,
          );
        });
      }
    }
    const filteredClients = this.filterClients(contestantClients, filter);
    return {
      filteredClients,
      totalClients: allClients.length,
    };
  }
}

export const filterService = new ClientFilterService();
